import { activeFestivalEditionQuery } from '@/api/queries';
import { FestivalTicketOrder } from '@/features/ticketing/FestivalTicketOrder';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isTicketingUnlocked, useTicketing } from '@/hooks/useTicketing';
import { Container } from '@/components';
import { formatEventDateTime } from '@/features/events/eventFormatting';

const StatePanel = ({ title, message }: { title: string; message: string }) => {
  const { t } = useTranslation();
  return (
    <Container max={760}>
      <h1 className="font-disp text-6xl uppercase text-ww-charcoal sm:text-7xl">{t('tickets')}</h1>
      <div
        className="mt-8 p-7"
        style={{
          background: 'var(--paper-50)',
          border: '2px solid var(--ww-charcoal)',
          boxShadow: '5px 5px 0 var(--ww-charcoal)',
        }}>
        <h2 className="font-sans text-2xl font-bold text-ww-charcoal">{title}</h2>
        <p className="mt-2 font-sans text-ink-soft">{message}</p>
      </div>
    </Container>
  );
};

export const TicketsPage = () => {
  const { t } = useTranslation();
  const { ticketToken } = useTicketing();
  const { data: activeEdition, isLoading } = useQuery(activeFestivalEditionQuery());

  if (isLoading) return null;

  if (!activeEdition) {
    return (
      <StatePanel title={t('tickets_not_open_title')} message={t('tickets_not_open_message')} />
    );
  }

  const unlocked = isTicketingUnlocked(activeEdition.accessToken, ticketToken);

  if (!unlocked) {
    return (
      <StatePanel title={t('tickets_not_open_title')} message={t('tickets_not_open_message')} />
    );
  }

  if (activeEdition.festivalSaleState !== 'open') {
    return <StatePanel title={t('tickets_soon_title')} message={t('tickets_prepared_message')} />;
  }

  const date = formatEventDateTime(activeEdition.startsAt, activeEdition.endsAt);
  const subtitle = [activeEdition.title, date, activeEdition.location].filter(Boolean).join(' · ');

  return (
    <Container max={1180}>
      <h1 className="font-disp text-6xl uppercase text-ww-charcoal sm:text-7xl">{t('tickets')}</h1>
      {subtitle ? (
        <p className="mb-9 mt-2 font-mono text-sm tracking-[0.04em] text-ink-soft">{subtitle}</p>
      ) : null}
      <FestivalTicketOrder
        context={{
          ownerInput: { kind: 'activeFestivalEdition' },
          ownerRef: { kind: 'festivalEdition', festivalEditionId: activeEdition.festivalEditionId },
          title: activeEdition.title || 'Unsere Tickets',
          subtitle: activeEdition.location,
          saleState: activeEdition.festivalSaleState,
          bookingFields: activeEdition.bookingFields,
          serviceFeePercent: activeEdition.settings?.serviceFeePercent,
        }}
      />
    </Container>
  );
};
